import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
}

/** Pose l'attribut data-theme sur <html> (lu par _variables.scss). */
function applyToDom(mode: ThemeMode): ResolvedTheme {
  const resolved = resolve(mode);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved);
  }
  return resolved;
}

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Bascule simple clair <-> sombre (fixe un mode explicite). */
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      resolved: resolve('system'),

      setMode: (mode) => set({ mode, resolved: applyToDom(mode) }),

      toggle: () => {
        const next: ThemeMode = get().resolved === 'dark' ? 'light' : 'dark';
        set({ mode: next, resolved: applyToDom(next) });
      },
    }),
    {
      name: 'recubebe-theme',
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) applyToDom(state.mode);
      },
    }
  )
);

/**
 * À appeler une fois au démarrage : applique le thème et suit les changements
 * système tant que l'utilisateur est en mode 'system'.
 */
export function initTheme(): void {
  const { mode } = useThemeStore.getState();
  useThemeStore.setState({ resolved: applyToDom(mode) });

  if (typeof window !== 'undefined' && window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (useThemeStore.getState().mode === 'system') {
        useThemeStore.setState({ resolved: applyToDom('system') });
      }
    });
  }
}
